import { User, UserManager, type SignoutRedirectArgs, type UserManagerSettings } from 'oidc-client-ts'

type RefreshResponse = {
  access_token: string
  token_type: string
  expires_in: number
}

// Renew this many seconds before expiry, matching the library's renew lead.
const RENEW_LEEWAY_SECONDS = 60

type FetchFn = typeof fetch

// The refresh token lives in an HttpOnly cookie, so the library's silent-renew
// paths can't reach it (refresh_token grant needs a JS-readable token; the
// iframe path needs third-party cookies). Override renew/logout to hit auth-api's
// own cookie-authenticated /refresh and /logout instead.
export class AuthApiUserManager extends UserManager {
  private readonly authority: string
  // Injectable so tests mock the transport instead of stubbing global fetch.
  private readonly fetchFn: FetchFn
  // Rotation makes overlapping refreshes replay a consumed token (401); share one.
  private refreshInFlight: Promise<User | null> | null = null

  constructor(settings: UserManagerSettings, fetchFn: FetchFn = (input, init) => fetch(input, init)) {
    super(settings)
    this.authority = settings.authority
    this.fetchFn = fetchFn
  }

  async signinSilent(): Promise<User | null> {
    if (this.refreshInFlight) {
      return this.refreshInFlight
    }
    this.refreshInFlight = this.refresh()
    try {
      return await this.refreshInFlight
    } finally {
      this.refreshInFlight = null
    }
  }

  async getFreshToken({ force = false }: { force?: boolean } = {}): Promise<string | undefined> {
    const current = await this.getUser()
    if (!force && current?.access_token && !isNearExpiry(current)) {
      return current.access_token
    }
    const renewed = await this.signinSilent()
    return renewed?.access_token ?? current?.access_token
  }

  private async refresh(): Promise<User | null> {
    const current = await this.getUser()
    if (!current) {
      return null
    }

    const response = await this.fetchFn(`${this.authority}/refresh`, {
      method: 'POST',
      credentials: 'include'
    })
    if (!response.ok) {
      // Drop the session only on 401 (token truly invalid); a transient 5xx must not log the user out.
      if (response.status === 401) {
        await this.removeUser()
      }
      const body = await safeJson(response)
      throw new Error(`/refresh failed (${response.status}): ${JSON.stringify(body)}`)
    }
    const body = (await response.json()) as RefreshResponse

    const renewed = new User({
      id_token: current.id_token,
      session_state: current.session_state,
      access_token: body.access_token,
      token_type: body.token_type,
      scope: current.scope,
      profile: current.profile,
      expires_at: nowInSeconds() + body.expires_in
    })

    await this.storeUser(renewed)
    // storeUser is silent; raise userLoaded so react-oidc-context re-renders.
    void this.events.load(renewed)
    return renewed
  }

  async signoutRedirect(args?: SignoutRedirectArgs): Promise<void> {
    const response = await this.fetchFn(`${this.authority}/logout`, {
      method: 'POST',
      credentials: 'include'
    })
    if (!response.ok && response.status !== 204) {
      const body = await safeJson(response)
      throw new Error(`/logout failed (${response.status}): ${JSON.stringify(body)}`)
    }
    await this.removeUser()
    const target = args?.post_logout_redirect_uri ?? this.settings.post_logout_redirect_uri ?? '/'
    window.location.assign(target)
  }
}

function isNearExpiry(user: User): boolean {
  // expires_in is a live getter: seconds until the access token expires.
  return user.expires_in !== undefined && user.expires_in <= RENEW_LEEWAY_SECONDS
}

function nowInSeconds(): number {
  return Math.floor(Date.now() / 1000)
}

async function safeJson(response: Response): Promise<unknown> {
  try {
    return await response.json()
  } catch {
    return null
  }
}
